"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    ANCIEN_EMPLOYEUR,
    MARQUES,
    POUVOIRS,
    diagnostic,
    recapitulatif,
} from "@/lib/tools/employmentReclassification";
import { Avancer, Ecran, Liste, Recommencer, Resultat, useParcours } from "./Journey";

/**
 * La requalification en salariat : où se situe votre relation.
 *
 * AUCUN VERDICT, jamais. La qualification appartient au juge, et le parcours le
 * dit à l'écran de résultat comme dans son récapitulatif. Ce qu'il montre, c'est
 * lesquels des TROIS POUVOIRS de l'arrêt du 13 novembre 1996 la situation
 * touche, et ce que les juges en font.
 *
 * DES CASES, PAS DES QUESTIONS UNE PAR UNE. Onze indices posés un par écran
 * feraient onze écrans, et surtout donneraient à chacun un poids qu'il n'a pas :
 * un indice isolé ne caractérise rien. Ils se cochent donc par groupes, sous le
 * pouvoir qu'ils servent à établir, ce qui est exactement la façon dont un juge
 * les lit.
 *
 * RIEN N'EST COCHÉ D'AVANCE, et ne rien cocher est une réponse valable : c'est
 * même le résultat qu'on souhaite au lecteur.
 */

const L = {
    fr: {
        phases: ["Ce que votre client décide", "Ce que vous gardez", "Où vous en êtes"],
        retour: "Retour",
        prive: "Rien ne sort de cet onglet : vos réponses restent dans votre navigateur, et tout s'efface quand vous le fermez.",

        introTitre: "Ce que votre client peut vous imposer",
        introAide: "Depuis 1996, la Cour de cassation reconnaît le lien de subordination à trois pouvoirs : donner des ordres, en contrôler l'exécution, sanctionner les manquements. Cochez ce qui décrit votre relation avec ce client. Ne rien cocher est une réponse.",

        ordresTitre: "Donner des ordres et des directives",
        horaires: "Des horaires ou des jours de présence me sont imposés",
        methode: "On me dit comment faire, pas seulement ce qu'il faut livrer",
        tarif: "Mon tarif est fixé par le client, je ne le négocie pas",

        controleTitre: "Contrôler l'exécution",
        rendreCompte: "Je dois rendre compte régulièrement de mon avancement",
        materiel: "Le matériel et les logiciels que j'utilise sont fournis par le client",
        equipe: "Je suis intégré à une équipe et à un service organisés par le client",

        sanctionTitre: "Sanctionner les manquements",
        sanction: "Un manquement peut m'être reproché formellement, ou me coûter des missions",
        exclusivite: "Je ne peux pas travailler pour d'autres, en fait ou par une clause",

        marquesTitre: "Ce que vous gardez de votre indépendance",
        marquesAide: "Ces éléments n'annulent pas les précédents. Ce sont ceux que vous pourrez prouver, par des devis, des échanges ou des livrables, et c'est ce qu'un juge examine.",
        refuser: "Je peux refuser une mission sans que la relation s'arrête",
        remplacer: "Je peux me faire remplacer ou sous-traiter une partie du travail",
        livrable: "Je facture un livrable et un résultat, pas des journées de présence",
        clientele: "J'ai d'autres clients, ou je suis libre d'en chercher",
        outils: "Je travaille avec mes propres outils et depuis mon propre poste",

        ancienTitre: "Un dernier point, celui que les juges regardent en premier",
        ancienAide: "Facturer les mêmes tâches à celui qui était votre employeur est la situation la plus exposée. Elle n'est pas interdite, et le cumul d'un emploi salarié et d'une micro-entreprise est parfaitement légal.",
        ancienEmployeur: "Ce client est mon ancien ou mon actuel employeur, et je lui facture des tâches comparables",

        continuer: "Continuer",
        voirResultat: "Voir où j'en suis",
        resultatTitre: "Où se situe votre relation",
        recapTitre: "Le détail",
        recommencer: "Recommencer",

        verdictTitre: "Ce que nous ne pouvons pas vous dire",
        verdict: "Si votre relation sera requalifiée. La qualification d'un contrat appartient au juge, qui regarde les conditions de fait dans leur ensemble et non les indices un par un. Aucun calcul ne peut trancher à sa place, et les grilles de risque qui circulent ne reposent sur rien.",

        presomptionTitre: "L'immatriculation joue pour vous",
        presomption: "L'article L. 8221-6 du code du travail présume qu'une personne immatriculée comme travailleur indépendant n'est pas liée à son donneur d'ordre par un contrat de travail. C'est une présomption simple : elle tombe si un lien de subordination juridique permanente est établi. Mais c'est à celui qui invoque le contrat de travail de la renverser, jamais à vous de prouver votre indépendance.",

        troisTitre: "Ce que vos réponses touchent",
        tousLesPouvoirs: "Vos réponses décrivent les trois pouvoirs à la fois : des directives, un contrôle et une sanction possible. C'est exactement le faisceau que les juges retiennent, et c'est ce qui a été retenu pour un livreur à vélo en novembre 2018 puis pour un chauffeur en mars 2020. Cela ne préjuge pas de votre cas, mais cela mérite d'être regardé avec quelqu'un qui pourra le lire au vu des pièces.",
        deuxPouvoirs: "Vos réponses touchent deux des trois pouvoirs. Les juges apprécient le faisceau dans son ensemble et non pouvoir par pouvoir, mais deux sur trois est le niveau où la question se pose vraiment, et où il devient utile de reprendre la façon dont la relation est organisée.",
        unPouvoir: "Vos réponses touchent un seul des trois pouvoirs. Un indice isolé ne caractérise pas la subordination : ce qui compte est l'ensemble, et il n'y est pas. Corrigez tout de même ce point : aujourd'hui, cela ne vous coûte presque rien.",
        aucunPouvoir: "Vos réponses ne décrivent aucun des trois pouvoirs. La présomption d'indépendance attachée à votre immatriculation joue pleinement, et rien dans ce que vous décrivez ne la renverse.",

        ancienNote: "Vous facturez celui qui était votre employeur, pour des tâches comparables. Le cumul est légal et cette situation n'est pas interdite, mais c'est la première que les juges examinent, et celle où le faisceau se lit le plus vite. Les pièces qui montrent ce qui a changé depuis, dans l'organisation du travail comme dans les livrables, valent plus qu'une clause du contrat.",

        seuilTitre: "Le seuil qui n'existe pas",
        seuil: "Travailler pour un seul client est licite. Aucun texte ne fixe de nombre de clients ni de part de chiffre d'affaires qui déclencherait la requalification, et en avoir dix ne vous protège pas si la subordination est caractérisée avec l'un d'eux.",

        suiteTitre: "Et maintenant",
        suite1: "Un devis qui décrit un résultat, un délai et un prix pèse plus qu'une clause affirmant l'absence de lien de subordination : un contrat qui le déclare en toutes lettres ne vaut rien si les faits disent l'inverse.",
        suite2: "Gardez les échanges, les devis et les livrables. C'est cette matière que le juge examine, et elle ne se reconstitue pas après coup.",
        suite3: "Facturer au livrable plutôt qu'à la journée passée sur place change la nature de ce que vous vendez, et c'est l'un des rares points qui se corrigent du jour au lendemain.",
        suite4: "Deux juridictions peuvent s'en saisir sans juger la même chose : le conseil de prud'hommes reconstitue un contrat de travail, l'Urssaf recalcule des cotisations lors d'un contrôle chez votre client. L'attestation de vigilance qu'il vous réclame le couvre sur vos cotisations, jamais sur la nature de la relation.",

        source: "Règles relevées le 8 septembre 2026 : article L. 8221-6 du code du travail sur Légifrance, et la page du ministère du Travail sur le recours abusif aux travailleurs indépendants.",
    },
    en: {
        phases: ["What your client decides", "What you keep", "Where you stand"],
        retour: "Back",
        prive: "Nothing leaves this tab: your answers stay in your browser, and everything is erased when you close it.",

        introTitre: "What your client can impose on you",
        introAide: "Since 1996, the Cour de cassation has recognised legal subordination by three powers: giving orders, controlling their execution, sanctioning failures. Tick what describes your relationship with this client. Ticking nothing is an answer.",

        ordresTitre: "Giving orders and directives",
        horaires: "Set hours or days of attendance are imposed on me",
        methode: "I am told how to do it, not only what to deliver",
        tarif: "My rate is set by the client, I do not negotiate it",

        controleTitre: "Controlling execution",
        rendreCompte: "I have to report on my progress regularly",
        materiel: "The equipment and software I use are provided by the client",
        equipe: "I am integrated into a team and a department organised by the client",

        sanctionTitre: "Sanctioning failures",
        sanction: "A failure can be formally held against me, or cost me assignments",
        exclusivite: "I cannot work for others, in fact or under a clause",

        marquesTitre: "What you keep of your independence",
        marquesAide: "These do not cancel out the previous ones. They are what you will be able to prove, with quotations, exchanges or deliverables, and that is what a judge examines.",
        refuser: "I can turn down an assignment without the relationship ending",
        remplacer: "I can send a replacement or subcontract part of the work",
        livrable: "I invoice a deliverable and a result, not days spent on site",
        clientele: "I have other clients, or I am free to look for them",
        outils: "I work with my own tools and from my own workstation",

        ancienTitre: "One last point, the one judges look at first",
        ancienAide: "Invoicing the same tasks to the person who was your employer is the most exposed situation. It is not forbidden, and combining employment with a micro-enterprise is perfectly legal.",
        ancienEmployeur: "This client is my former or current employer, and I invoice them comparable tasks",

        continuer: "Continue",
        voirResultat: "See where I stand",
        resultatTitre: "Where your relationship stands",
        recapTitre: "The detail",
        recommencer: "Start again",

        verdictTitre: "What we cannot tell you",
        verdict: "Whether your relationship will be reclassified. Qualifying a contract belongs to the judge, who looks at the facts as a whole and not at indicators one by one. No calculation can decide in their place, and the risk grids in circulation rest on nothing.",

        presomptionTitre: "Registration works in your favour",
        presomption: "Article L. 8221-6 of the labour code presumes that a person registered as self-employed is not bound to their client by an employment contract. It is a rebuttable presumption: it falls if permanent legal subordination is established. But it is for whoever claims an employment contract to rebut it, never for you to prove your independence.",

        troisTitre: "What your answers touch",
        tousLesPouvoirs: "Your answers describe all three powers at once: directives, control and possible sanction. That is exactly the set of indicators judges rely on, and it is what was held for a bicycle courier in November 2018 and then for a driver in March 2020. It does not prejudge your case, but it deserves to be looked at with someone who can read it against the documents.",
        deuxPouvoirs: "Your answers touch two of the three powers. Judges assess the indicators as a whole rather than power by power, but two out of three is the level at which the question genuinely arises, and where it becomes worth revisiting how the relationship is organised.",
        unPouvoir: "Your answers touch only one of the three powers. An isolated indicator does not characterise subordination: what counts is the whole, and it is not there. Correct that point anyway: today it costs you almost nothing.",
        aucunPouvoir: "Your answers describe none of the three powers. The presumption of independence attached to your registration applies fully, and nothing you describe rebuts it.",

        ancienNote: "You invoice the person who was your employer, for comparable tasks. Combining the two is legal and this situation is not forbidden, but it is the first one judges examine, and the one where the indicators read fastest. Documents showing what has changed since, in how the work is organised as much as in the deliverables, are worth more than a clause in the contract.",

        seuilTitre: "The threshold that does not exist",
        seuil: "Working for a single client is lawful. No statute sets a number of clients or a share of turnover that would trigger reclassification, and having ten clients does not protect you if subordination is characterised with one of them.",

        suiteTitre: "What happens next",
        suite1: "A quotation describing a result, a deadline and a price weighs more than a clause stating the absence of subordination: a contract that says so in as many words is worth nothing if the facts say otherwise.",
        suite2: "Keep the exchanges, the quotations and the deliverables. That is the material a judge examines, and it cannot be reconstructed after the fact.",
        suite3: "Invoicing a deliverable rather than days spent on site changes the nature of what you sell, and it is one of the few points that can be corrected overnight.",
        suite4: "Two jurisdictions can take it up without judging the same thing: the labour court reconstructs an employment contract, URSSAF recalculates contributions during an audit at your client's. The compliance certificate they ask you for covers them on your contributions, never on the nature of the relationship.",

        source: "Rules recorded on 8 September 2026: article L. 8221-6 of the labour code on Légifrance, and the labour ministry's page on the abusive use of self-employed workers.",
    },
};

const PHASES_PAR_ECRAN = { indices: 0, marques: 1, ancien: 1, resultat: 2 };

const VIDE = { repondu: false };

/**
 * Une case à cocher, sobre : la case, son libellé, et rien d'autre.
 *
 * Onze indices posés un par écran donneraient à chacun un poids qu'il n'a pas.
 */
function Case({ coche, libelle, onToggle }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-pressed={coche}
            className={`group w-full text-left flex items-start gap-3 px-4 py-3 rounded-xl border transition-colors duration-200 ${
                coche
                    ? "border-primary/60 bg-primary/[0.05]"
                    : "border-gray-200/70 dark:border-white/10 hover:border-primary/40"
            }`}
        >
            <SolarIcon
                name={coche ? "check-square-bold" : "stop-linear"}
                className={`mt-0.5 text-xl shrink-0 transition-colors duration-200 ${
                    coche ? "text-primary" : "text-gray-300 dark:text-white/25"
                }`}
            />
            <span className="text-[15px] leading-6 dark:text-white">{libelle}</span>
        </button>
    );
}

/** Un groupe de cases sous l'intitulé du pouvoir qu'elles servent à établir. */
function Groupe({ titre, cles, t, r, basculer }) {
    return (
        <div>
            <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-2.5">
                {titre}
            </div>
            <div className="space-y-2">
                {cles.map((cle) => (
                    <Case key={cle} coche={Boolean(r[cle])} libelle={t[cle]} onToggle={basculer(cle)} />
                ))}
            </div>
        </div>
    );
}

export default function Requalification({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [r, setR] = useState(VIDE);

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "indices",
        () => setR(VIDE),
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    const basculer = (cle) => () => setR((p) => ({ ...p, [cle]: !p[cle] }));
    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };

    const etat = useMemo(() => diagnostic(r), [r]);
    const recap = useMemo(() => recapitulatif(r), [r]);

    const nomPouvoir = { ordres: t.ordresTitre, controle: t.controleTitre, sanction: t.sanctionTitre };

    if (etape === "indices") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.introTitre} aide={t.introAide}>
                <div className="space-y-6">
                    {POUVOIRS.map((p) => (
                        <Groupe key={p.cle} titre={nomPouvoir[p.cle]} cles={p.indices} t={t} r={r} basculer={basculer} />
                    ))}
                </div>
                <Avancer pleineLargeur onClick={() => aller("marques")}>
                    {t.continuer}
                </Avancer>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "marques") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.marquesTitre} aide={t.marquesAide}>
                <div className="space-y-2">
                    {MARQUES.map((cle) => (
                        <Case key={cle} coche={Boolean(r[cle])} libelle={t[cle]} onToggle={basculer(cle)} />
                    ))}
                </div>
                <Avancer pleineLargeur onClick={() => aller("ancien")}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "ancien") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.ancienTitre} aide={t.ancienAide}>
                <Case
                    coche={Boolean(r[ANCIEN_EMPLOYEUR])}
                    libelle={t.ancienEmployeur}
                    onToggle={basculer(ANCIEN_EMPLOYEUR)}
                />
                <Avancer
                    pleineLargeur
                    onClick={() => {
                        setR((p) => ({ ...p, repondu: true }));
                        aller("resultat");
                    }}
                >
                    {t.voirResultat}
                </Avancer>
            </Ecran>
        );
    }

    /* ---------------------------------------------------------------- */
    /* Le résultat, qui décrit et ne tranche pas                          */
    /* ---------------------------------------------------------------- */

    const combien = etat ? etat.pouvoirsTouches.length : 0;
    const lecture = etat
        ? [t.aucunPouvoir, t.unPouvoir, t.deuxPouvoirs, t.tousLesPouvoirs][combien]
        : "";

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            <div className="mt-4">
                <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 p-5 mb-6">
                    <div className="flex items-center gap-2.5 mb-3">
                        <SolarIcon
                            name="scale-linear"
                            className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                        />
                        <div className="font-semibold dark:text-white leading-snug">{t.troisTitre}</div>
                    </div>
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">{lecture}</p>
                </div>

                {etat && etat.ancienEmployeur && (
                    <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                        <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">{t.ancienNote}</p>
                    </div>
                )}

                <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                    {t.recapTitre}
                </div>
                <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden mb-6">
                    <table className="w-full text-sm">
                        <tbody>
                            {(recap || []).map(([titre, valeur]) => (
                                <tr
                                    key={titre}
                                    className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10"
                                >
                                    <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-2/5">
                                        {titre}
                                    </td>
                                    <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mb-5 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                    <div className="flex items-center gap-2.5 mb-3">
                        <SolarIcon
                            name="shield-check-linear"
                            className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                        />
                        <div className="font-semibold dark:text-white leading-snug">{t.presomptionTitre}</div>
                    </div>
                    <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.presomption}</p>
                </div>

                <div className="mb-5 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                    <div className="flex items-center gap-2.5 mb-3">
                        <SolarIcon
                            name="minus-circle-linear"
                            className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                        />
                        <div className="font-semibold dark:text-white leading-snug">{t.verdictTitre}</div>
                    </div>
                    <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.verdict}</p>
                </div>

                <div className="mb-5 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                    <div className="flex items-center gap-2.5 mb-3">
                        <SolarIcon
                            name="users-group-rounded-linear"
                            className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                        />
                        <div className="font-semibold dark:text-white leading-snug">{t.seuilTitre}</div>
                    </div>
                    <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.seuil}</p>
                </div>

                <div className="mt-8">
                    <div className="flex items-center gap-2.5 mb-4">
                        <SolarIcon
                            name="clipboard-check-linear"
                            className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                        />
                        <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                    </div>
                    <Liste items={[t.suite1, t.suite2, t.suite3, t.suite4]} />
                </div>

                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0">{t.source}</p>
                <Recommencer onClick={recommencer} libelle={t.recommencer} />
            </div>
        </Resultat>
    );
}
